import { API } from "@swivl-mono/api-helper";
import { AppInterface, AppType, LocationVarKey, RoutingGroupInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Input, OldPanel, Select, SelectOption } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { useNavigate } from "react-router-dom";

import { Tooltip } from "../../../Components/Tooltip/Tooltip";
import AppsModel from "../../../Models/Apps/Apps.Model";
import { Member } from "../../../Models/Member/Member.Current";
import GoogleAppView from "./Google/Google.AppView";
import MandrillAppView from "./Mandrill/Mandrill.AppView";
import { showPurchaseNumberModal } from "./Twilio/PurchaseNumberModal";
import TwilioAccountSettings from "./Twilio/TwilioAccountSettings";
import TwilioView from "./Twilio/TwilioView";
import WhatsAppView from "./WhatsApp/WhatsAppView";

interface Props {
  type?: AppType;
  appId?: string;
}

function imageForAppType(type: AppType) {
  switch (type) {
    case AppType.GOOGLE:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/app-google.png";
    case AppType.HUBSPOT:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/hubspotlogo-web-color.png";

    case AppType.MANDRILL:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/app-mandrill.png";
    // case AppType.SLACK:
    //   return "https://swivl-cdn.s3-us-west-1.amazonaws.com/Slack_RGB.png";
    case AppType.TWILIO:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/twilio-logo-red.png";
    case AppType.FACEBOOK_MESSENGER:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/app-fbmessenger.png";
    case AppType.WHATSAPP:
      return "https://swivl-cdn.s3-us-west-1.amazonaws.com/app-whatsapp.png";
    default:
      return "";
  }
}

function appContentForType(app: AppInterface, selected: boolean, disabled?: boolean) {
  switch (app.type) {
    case AppType.GOOGLE:
      return <GoogleAppView app={app} selected={selected} />;
    case AppType.HUBSPOT:
      return <div>Hubspot Content</div>;

    case AppType.MANDRILL:
      return <MandrillAppView app={app} selected={selected} disabled={disabled} />;
    // case AppType.SLACK:
    //   return <div>Slack Content</div>;
    case AppType.TWILIO:
      return <TwilioView app={app} selected={selected} disabled={disabled} />;
    // case AppType.FACEBOOK_MESSENGER:
    //   return <FBMView app={app} selected={selected} />;
    case AppType.WHATSAPP:
      return <WhatsAppView app={app} selected={selected} />;
    default:
      return "";
  }
}
const NO_LOCK_VALUE = "";

interface GroupLockFieldProps {
  app: AppInterface;
  disabled?: boolean;
}

// Lets an operator lock a Twilio number to a single routing group. The value is
// stored on the App's free-form `data` JSON as `limitToGroupId`; the bot reads
// it on voice setup and enforces the lock via every group-aware tool. Gated by
// the Snowy feature flag by the caller.
function GroupLockField({ app, disabled }: GroupLockFieldProps) {
  const [groups, setGroups] = useState<RoutingGroupInterface[]>([]);

  useEffect(() => {
    const loadGroups = async () => {
      if (!Member.orgId) {
        return;
      }
      const loaded = await API.RoutingGroup.listGroups(Member.orgId);
      setGroups(loaded);
    };
    loadGroups();
  }, []);

  const options: SelectOption[] = useMemo(() => {
    return [{ label: "No lock / all groups", value: NO_LOCK_VALUE }, ...groups.map((group) => ({ label: group.name, value: String(group.id) }))];
  }, [groups]);

  const limitToGroupId: unknown = app.data?.limitToGroupId;
  const selectedValue = limitToGroupId != null ? String(limitToGroupId) : NO_LOCK_VALUE;

  const handleChange = (selected: string[]) => {
    const value = selected[0] ?? NO_LOCK_VALUE;
    const nextData = { ...(app.data ?? {}) };
    if (value === NO_LOCK_VALUE) {
      delete nextData.limitToGroupId;
    } else {
      nextData.limitToGroupId = Number(value);
    }
    app.data = nextData;
    AppsModel.update(app, false);
  };

  return (
    <div className="mb-3 flex flex-col gap-1">
      <label>Group lock</label>
      {disabled ? (
        <p className="body-sm text-grey-900">{options.find((o) => o.value === selectedValue)?.label ?? "—"}</p>
      ) : (
        <Select options={options} selectedValues={[selectedValue]} hideClear onChange={handleChange} />
      )}
      <span className="text-sm text-grey-500">
        Locking a number routes all of that number&apos;s calls and escalations to the selected group only.
      </span>
    </div>
  );
}

interface AppInstancePanelBodyProps {
  app: AppInterface;
  selected: boolean;
  onRemove: () => void;
}

// Edits stay local until Save; Cancel restores the snapshot taken at Edit.
function AppInstancePanelBody({ app, selected, onRemove }: AppInstancePanelBodyProps): React.ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const snapshotRef = useRef<AppInterface | null>(null);

  const handleEdit = (): void => {
    snapshotRef.current = structuredClone(app);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    if (snapshotRef.current) AppsModel.update(snapshotRef.current, false);
    snapshotRef.current = null;
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      await AppsModel.update(app, true);
      snapshotRef.current = null;
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <img src={imageForAppType(app.type)} width={100} height={46} alt={app.type} />
        {isEditing ? (
          <div className="flex gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={handleCancel}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={saving} onClickAsync={handleSave}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <Button color={ButtonColor.BLACK} size={ButtonSize.SMALL} onClick={handleEdit}>
            Edit
            <BiEditAlt className="size-4" />
          </Button>
        )}
      </div>

      <div className="mb-3">
        <label>Name</label>
        {isEditing ? (
          <Input
            value={app.name}
            onChange={(e) => {
              app.name = e.target.value;
              AppsModel.update(app, false);
            }}
          />
        ) : (
          <p className="body-sm text-grey-900">{app.name || "—"}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {appContentForType(app, selected, !isEditing)}
        {Member.isSnowyEnabled && app.type === AppType.TWILIO && <GroupLockField app={app} disabled={!isEditing} />}
        <div className="flex justify-end">
          <Button variant={ButtonVariant.GHOST} onClick={onRemove}>
            Remove Instance
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AppsDetailsView({ type, appId }: Props) {
  const { apps, twilioSetup } = AppsModel.useState();
  const navigate = useNavigate();
  if (!type) {
    return <div>Invalid App Type</div>;
  }
  const appsOfType = useMemo(() => {
    if (!apps) {
      return undefined;
    }
    return apps.filter((app) => app.type === type).filter((app) => app.active);
  }, [apps, type]);
  const selectedAppId = appId ? parseInt(appId) : undefined;

  // The floating Snowy number, if it's in this org.
  const snowyNumberInOrg = useMemo(() => appsOfType?.find((app) => app.data?.snowyFloat === true), [appsOfType]);

  const [errorReason, setErrorReason] = useState("");

  // Determine Twilio button state
  const hasSubaccount = !!twilioSetup?.vars?.[LocationVarKey.TwilioSubaccountSID];
  const hasTwilioApps = appsOfType && appsOfType.length > 0;
  const hasPostalCode = !!twilioSetup?.vars?.[LocationVarKey.PostalCode];

  // Warn when apps have mixed accountSids, i.e. some on parent, some on subaccount.
  // This breaks voice so we're trying to get ahead of it.
  const hasMismatchedCredentials = useMemo(() => {
    if (!appsOfType || appsOfType.length <= 1) return false;
    const sids = appsOfType.map((app) => app.token?.accountSid).filter(Boolean);
    return new Set(sids).size > 1;
  }, [appsOfType]);

  // Warn when a subaccount is configured but none of the org's API keys lives inside
  // it, so the Contact Center v3 softphone cannot register there (ENG-3747). The server
  // answers this against Twilio; the Outbound pair staying a parent-account key is
  // correct for the legacy softphone and is NOT a problem.
  const needsSoftphoneKey = hasSubaccount && twilioSetup?.softphoneKeyInsideSubaccount === false;

  // Button state logic:
  // - "Buy first number": No subaccount AND no apps (runs full setup)
  // - "Buy new number": Anything else (open purchase modal)
  type TwilioButtonState = "buy_first" | "buy_new";

  const twilioButtonState: TwilioButtonState = useMemo(() => {
    if (!hasSubaccount && !hasTwilioApps) {
      return "buy_first";
    } else {
      return "buy_new";
    }
  }, [hasSubaccount, hasTwilioApps]);

  const canBuyFirstNumber = twilioButtonState === "buy_first" && hasPostalCode;

  useEffect(() => {
    const loadTwilioSetup = async () => {
      await AppsModel.loadTwilioSetup();

      // Set error reason for "Buy first number" button
      if (twilioButtonState === "buy_first" && !hasPostalCode) {
        setErrorReason("A postal code must be set in the location settings before you can buy a phone number");
      } else {
        setErrorReason("");
      }
    };
    loadTwilioSetup();
  }, [apps, twilioButtonState, hasPostalCode]);

  return (
    <div className="flex flex-col p-5">
      <div className="flex items-center justify-between">
        <img src={imageForAppType(type)} />
        <div className="flex gap-2">
          {Member.isSwivlStaff && type === AppType.TWILIO && twilioButtonState === "buy_first" && (
            <Tooltip classNames={{ tooltip: "text-xs" }} content={errorReason || ""} placement="bottom" disabled={canBuyFirstNumber}>
              <Button
                disabled={!canBuyFirstNumber}
                onClickAsync={async () => {
                  const app = await AppsModel.setupTwilio();
                  if (app) {
                    await AppsModel.load(true);
                    navigate(`/settings/apps/${type}/${app.id}`);
                  }
                }}
              >
                Buy first number
              </Button>
            </Tooltip>
          )}
          {/* Also shown without a subaccount; modal then buys on parent */}
          {Member.isSwivlStaff && type === AppType.TWILIO && twilioButtonState === "buy_new" && (
            <Button
              onClick={() => {
                showPurchaseNumberModal((appId, phoneNumber) => {
                  // Navigate to the newly created app
                  navigate(`/settings/apps/${type}/${appId}`);
                });
              }}
            >
              Buy new number
            </Button>
          )}
          {Member.isSwivlStaff &&
            type === AppType.TWILIO &&
            (snowyNumberInOrg ? (
              <Button
                onClickAsync={async () => {
                  if (!window.confirm("This will release the test number. Continue?")) {
                    return;
                  }
                  snowyNumberInOrg.active = false;
                  await AppsModel.update(snowyNumberInOrg, true);
                  await AppsModel.load(true);
                }}
              >
                Release Test Number
              </Button>
            ) : (
              <Button
                onClickAsync={async () => {
                  if (!Member.orgId) {
                    return;
                  }
                  if (!window.confirm("This will move the test number into this org for testing. Are you sure you'd like to proceed?")) {
                    return;
                  }
                  await API.Apps.claimSnowyNumber(Member.orgId);
                  await AppsModel.load(true);
                }}
              >
                Test New System
              </Button>
            ))}
          <Button
            onClickAsync={async () => {
              const app = await AppsModel.createApp(type);
              navigate(`/settings/apps/${type}/${app.id}`);
            }}
          >
            + Add Instance
          </Button>
        </div>
      </div>

      {Member.isSwivlStaff && type === AppType.TWILIO && Member.orgId && (
        <div className="mt-5">
          <TwilioAccountSettings orgId={Member.orgId} />
        </div>
      )}

      {Member.isSwivlStaff && type === AppType.TWILIO && hasMismatchedCredentials && (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <strong>Numbers on two Twilio accounts:</strong> some of these numbers are on the main account and some on the subaccount. The bot and
          Contact Center v3 handle this. The <strong>legacy</strong> Contact Center softphone registers on the default outbound number's account only,
          so legacy voice escalations on numbers from the other account will not reach agents.
        </div>
      )}
      {Member.isSwivlStaff && type === AppType.TWILIO && needsSoftphoneKey && (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <strong>No Contact Center softphone key in the subaccount:</strong> a subaccount is configured, but none of this org's API keys lives inside
          it, so agents cannot take calls on subaccount numbers in Contact Center v3. Re-select the subaccount in <strong>Subaccount Settings</strong>{" "}
          above to create one.
        </div>
      )}

      <div className="mt-5 flex flex-col">
        {appsOfType?.map((app) => {
          const selected = selectedAppId && app.id === selectedAppId ? true : false;

          return (
            <OldPanel
              key={app.id}
              open={selected}
              onToggle={() => {
                if (selected) {
                  navigate(`/settings/apps/${type}`);
                } else {
                  navigate(`/settings/apps/${type}/${app.id}`);
                }
              }}
              header={<h4 className="m-0">{app.name}</h4>}
            >
              {app.type === AppType.TWILIO || app.type === AppType.MANDRILL ? (
                <AppInstancePanelBody
                  app={app}
                  selected={selected}
                  onRemove={() => {
                    if (window.confirm("Are you sure you want to delete this app?")) {
                      AppsModel.deactivateApp(app);
                      navigate(`/settings/apps/${type}`);
                    }
                  }}
                />
              ) : (
                <div className="flex flex-col">
                  <div className="mb-3">
                    <Input
                      value={app.name}
                      label="Name"
                      onChange={(e) => {
                        app.name = e.target.value;
                        AppsModel.update(app, false);
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    {appContentForType(app, selected)}
                    <div className="flex justify-end">
                      <Button
                        variant={ButtonVariant.GHOST}
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this app?")) {
                            AppsModel.deactivateApp(app);
                            navigate(`/settings/apps/${type}`);
                          }
                        }}
                      >
                        Remove Instance
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </OldPanel>
          );
        })}
      </div>
    </div>
  );
}
